import { z } from 'zod';

var Services;
(function (Services) {
    Services["PROGRAM_OWNER"] = "program_owner";
    Services["DOMAINS"] = "domains";
    Services["SALE"] = "sale";
    Services["DELIVERY"] = "delivery";
    Services["PICKUP"] = "pickup";
    Services["ASSEMBLY"] = "assembly";
    Services["INSPECTION"] = "inspection";
    Services["TITLING"] = "titling";
    Services["REGISTRATION"] = "registration";
    Services["FINANCING"] = "financing";
    Services["FINANCING_BROKERAGE"] = "financing_brokerage";
    Services["LICENSURE"] = "licensure";
    Services["TARIFF"] = "tariff";
    Services["INVENTORY"] = "inventory";
    Services["FREIGHT"] = "freight";
    Services["PROCESSING_FEES"] = "processing_fees";
    Services["COVERAGE_FULFILLMENT"] = "coverage_fulfillment";
    Services["COVERAGE_TAX"] = "coverage_tax";
    Services["EXTERNAL_TAX"] = "external_tax";
    Services["EXTERNAL_FEES"] = "external_fees";
    Services["ORIGINATOR"] = "originator";
    Services["TEMP_TAG"] = "temp_tag";
    // Internal only Services
    Services["PLATFORM"] = "platform";
    Services["FINANCING_BROKERAGE_PLATFORM"] = "financing_brokerage_platform";
    Services["FINANCING_BROKERAGE_SUBVENTION"] = "financing_brokerage_subvention";
    Services["INTERNAL_FEES"] = "internal_fees";
    Services["COVERAGE_VENDOR"] = "coverage_vendor";
    Services["COVERAGE_PLATFORM"] = "coverage_platform";
    Services["BDC"] = "bdc";
})(Services || (Services = {}));
const servicesValues = [
    Services.PROGRAM_OWNER,
    Services.DOMAINS,
    Services.SALE,
    Services.DELIVERY,
    Services.PICKUP,
    Services.ASSEMBLY,
    Services.INSPECTION,
    Services.TITLING,
    Services.REGISTRATION,
    Services.FINANCING,
    Services.FINANCING_BROKERAGE,
    Services.FINANCING_BROKERAGE_PLATFORM,
    Services.FINANCING_BROKERAGE_SUBVENTION,
    Services.LICENSURE,
    Services.TARIFF,
    Services.INVENTORY,
    Services.FREIGHT,
    Services.PROCESSING_FEES,
    Services.COVERAGE_FULFILLMENT,
    Services.COVERAGE_TAX,
    Services.EXTERNAL_TAX,
    Services.EXTERNAL_FEES,
    Services.ORIGINATOR,
    Services.TEMP_TAG,
    Services.PLATFORM,
    Services.INTERNAL_FEES,
    Services.COVERAGE_VENDOR,
    Services.COVERAGE_PLATFORM,
    Services.BDC,
];
const ServicesSchema = z.enum(servicesValues);
const SERVICE_COPY = {
    [Services.PROGRAM_OWNER]: {
        title: 'Ekho Program',
        briefDescription: 'For managing your program with Ekho',
        detailedDescription: 'For managing your program with Ekho. This service is required for all clients who use Ekho products to sell vehicles.',
        requirements: ['Active contract with Ekho'],
    },
    [Services.DOMAINS]: {
        title: 'Domains',
        briefDescription: 'For managing your domains',
        detailedDescription: 'For managing your domains',
        requirements: ['Valid subdomain registration'],
    },
    [Services.SALE]: {
        title: 'Titled Vehicle Sales',
        briefDescription: 'For titling and registering motor vehicles with the DMV.',
        detailedDescription: 'For titling and registering motor vehicles with the DMV.',
        requirements: ['Valid dealer license', 'Compliance with state regulations'],
    },
    [Services.DELIVERY]: {
        title: 'Vehicle Delivery',
        briefDescription: "80% of Ekho shoppers choose delivery. You're likely to sell more units if you offer delivery.",
        detailedDescription: "80% of Ekho shoppers choose delivery. You're likely to sell more units if you offer delivery.",
        requirements: ['Insurance coverage', 'Qualified drivers'],
    },
    [Services.PICKUP]: {
        title: 'Vehicle Pickup',
        briefDescription: 'Allow customers to pick up vehicles at your dealership.',
        detailedDescription: 'Allow customers to pick up vehicles at your dealership.',
        requirements: ['Designated pickup area', 'Customer service staff'],
    },
    [Services.ASSEMBLY]: {
        title: 'Vehicle Assembly/Installation',
        briefDescription: 'Offer assembly and installation of parts/accessories as part of vehicle sales',
        detailedDescription: 'Offer assembly and installation of parts/accessories as part of vehicle sales',
        requirements: ['Certified technicians', 'Proper assembly facilities'],
    },
    [Services.INSPECTION]: {
        title: 'Vehicle Inspection',
        briefDescription: 'Comprehensive vehicle inspection',
        detailedDescription: 'Thorough inspection of vehicle condition, safety features, and mechanical components before delivery',
        requirements: ['Certified inspectors', 'Standardized inspection protocol'],
    },
    [Services.TITLING]: {
        title: 'Vehicle Titling Service',
        briefDescription: 'Process vehicle title transfers',
        detailedDescription: 'Handle all aspects of vehicle title transfers including documentation and state requirements',
        requirements: ['Knowledge of state titling regulations', 'Processing capabilities'],
    },
    [Services.REGISTRATION]: {
        title: 'Vehicle Registration Service',
        briefDescription: 'Complete vehicle registration process',
        detailedDescription: 'Process vehicle registrations including plates, stickers, and required documentation',
        requirements: ['Knowledge of registration requirements', 'State authorization'],
    },
    [Services.TARIFF]: {
        title: 'Vehicle Import Service',
        briefDescription: 'Handle vehicle import requirements',
        detailedDescription: 'Process and manage vehicle import duties, taxes, and compliance requirements',
        requirements: ['Import/export knowledge', 'Customs broker relationships'],
    },
    [Services.INVENTORY]: {
        title: 'Vehicle Inventory Service',
        briefDescription: 'Manage vehicle inventory',
        detailedDescription: 'Track and manage vehicle inventory including storage, maintenance, and documentation',
        requirements: ['Secure storage facilities', 'Inventory management system'],
    },
    [Services.FREIGHT]: {
        title: 'Vehicle Freight Service',
        briefDescription: 'Long-distance vehicle transportation',
        detailedDescription: 'Coordinate and manage long-distance vehicle transportation via truck, rail, or other freight services',
        requirements: ['Freight carrier relationships', 'Transportation insurance'],
    },
    [Services.PROCESSING_FEES]: {
        title: 'Processing Fee Service',
        briefDescription: 'Handle transaction processing fees',
        detailedDescription: 'Management and processing of administrative fees related to vehicle transactions',
        requirements: ['Fee schedule compliance', 'Transaction processing system'],
    },
    [Services.COVERAGE_FULFILLMENT]: {
        title: 'Coverage Fulfillment Service',
        briefDescription: 'Process vehicle coverage plans',
        detailedDescription: 'Handle and process vehicle warranty and protection plan fulfillment',
        requirements: ['Coverage provider relationships', 'Processing capabilities'],
    },
    [Services.COVERAGE_TAX]: {
        title: 'Coverage Tax Service',
        briefDescription: 'Process coverage-related taxes',
        detailedDescription: 'Calculate and process taxes related to vehicle coverage and protection plans',
        requirements: ['Tax calculation capabilities', 'Compliance knowledge'],
    },
    [Services.EXTERNAL_TAX]: {
        title: 'External Tax Service',
        briefDescription: 'Process external vehicle taxes',
        detailedDescription: 'Calculate and process external taxes related to vehicle sales and transfers',
        requirements: ['Tax jurisdiction knowledge', 'Processing capabilities'],
    },
    [Services.EXTERNAL_FEES]: {
        title: 'External Fee Service',
        briefDescription: 'Handle external transaction fees',
        detailedDescription: 'Process and manage external fees related to vehicle transactions and services',
        requirements: ['Fee schedule knowledge', 'Processing capabilities'],
    },
    [Services.FINANCING_BROKERAGE]: {
        title: 'Financing Brokerage Service',
        briefDescription: 'Handle financing brokerage services',
        detailedDescription: 'Process and manage financing brokerage services',
        requirements: ['Financing brokerage knowledge', 'Processing capabilities'],
    },
    [Services.ORIGINATOR]: {
        title: 'Originator Service',
        briefDescription: 'Original service provider',
        detailedDescription: 'Handles initial service provision and coordination',
        requirements: ['Service provider certification'],
    },
    [Services.TEMP_TAG]: {
        title: 'Temporary Tag Service',
        briefDescription: 'Generate temporary registration tags',
        detailedDescription: 'Handle temporary tag generation for vehicles awaiting permanent registration',
        requirements: ['Tag issuance capabilities'],
    },
};

export { SERVICE_COPY, Services, ServicesSchema };
//# sourceMappingURL=Services.js.map
